import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Shield,
  ShieldOff,
  Key,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  Calendar,
  Activity,
  Save,
  Edit3,
  UserCog,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AdminUser, SubscriptionPlan } from '../../types';

interface EditForm {
  name: string;
  email: string;
  phone: string;
  subscription_plan: SubscriptionPlan;
}

export const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState({ limit: 20, offset: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { startImpersonation } = useAuth();
  const [impersonatingId, setImpersonatingId] = useState<number | null>(null);

  // Suplantar a un usuario: el backend rechaza hacerlo con otro admin, asi que
  // el boton solo se ofrece para cuentas normales.
  const handleImpersonate = async (user: AdminUser) => {
    const confirmed = window.confirm(
      `Vas a ver la aplicación como ${user.name}.\n\n` +
      'Podrás volver a tu cuenta con el aviso naranja de la parte superior.'
    );
    if (!confirmed) return;

    setImpersonatingId(user.id);
    try {
      await startImpersonation(user.id);
      window.location.href = '/';
    } catch (err: any) {
      alert(err?.message || 'No se pudo iniciar la suplantación');
      setImpersonatingId(null);
    }
  };

  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [userStats, setUserStats] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({ name: '', email: '', phone: '', subscription_plan: 'free' });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    loadUsers();
  }, [pagination.offset]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getUsers({
        limit: pagination.limit,
        offset: pagination.offset,
      });

      if (response.success) {
        setUsers(response.data.users);
        setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewUser = async (user: AdminUser) => {
    setSelectedUser(user);
    setShowModal(true);
    setTempPassword('');
    setIsEditing(false);
    setSaveSuccess(false);
    setSaveError('');
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: (user as any).phone || '',
      subscription_plan: user.subscription_plan || 'free',
    });

    try {
      const response = await adminAPI.getUser(user.id);
      if (response.success) {
        setUserStats(response.data.stats);
        // Update form with full user data
        const fullUser = response.data.user;
        setEditForm({
          name: fullUser.name || '',
          email: fullUser.email || '',
          phone: (fullUser as any).phone || '',
          subscription_plan: fullUser.subscription_plan || 'free',
        });
      }
    } catch (error) {
      console.error('Error loading user details:', error);
    }
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    setActionLoading('save');
    setSaveError('');
    setSaveSuccess(false);

    try {
      const response = await adminAPI.updateUser(selectedUser.id, {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        subscription_plan: editForm.subscription_plan,
      } as any);

      if (response.success) {
        // Update local state
        const updatedUser = { ...selectedUser, ...editForm };
        setSelectedUser(updatedUser);
        setUsers(users.map(u => u.id === selectedUser.id ? { ...u, ...editForm } : u));
        setSaveSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError('Error al guardar los cambios');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setSaveError('Error al guardar los cambios');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (user: AdminUser) => {
    setActionLoading(`admin-${user.id}`);
    try {
      const response = await adminAPI.toggleAdmin(user.id);
      if (response.success) {
        setUsers(users.map(u =>
          u.id === user.id ? { ...u, is_admin: !u.is_admin } : u
        ));
        if (selectedUser?.id === user.id) {
          setSelectedUser({ ...selectedUser, is_admin: !selectedUser.is_admin });
        }
      }
    } catch (error) {
      console.error('Error toggling admin:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (user: AdminUser) => {
    setActionLoading(`password-${user.id}`);
    try {
      const response = await adminAPI.resetUserPassword(user.id);
      if (response.success) {
        setTempPassword(response.data.tempPassword);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (user: AdminUser) => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${user.name}? Esta acción no se puede deshacer.`)) {
      return;
    }

    setActionLoading(`delete-${user.id}`);
    try {
      const response = await adminAPI.deleteUser(user.id);
      if (response.success) {
        setUsers(users.filter(u => u.id !== user.id));
        setShowModal(false);
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text)]">Usuarios</h1>
          <p className="text-[var(--color-text-muted)]">{pagination.total} usuarios registrados</p>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar usuarios..."
            className="input pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-[var(--color-text-muted)]">Usuario</th>
                <th className="hidden md:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Email</th>
                <th className="hidden lg:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Registro</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-[var(--color-text-muted)]">Plan</th>
                <th className="hidden sm:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Admin</th>
                <th className="text-right p-3 sm:p-4 text-sm font-medium text-[var(--color-text-muted)]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center">
                    <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-[var(--color-text-muted)]">
                    No se encontraron usuarios
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] cursor-pointer transition-colors"
                    onClick={() => handleViewUser(user)}
                  >
                    <td className="p-3 sm:p-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 shrink-0 rounded-full bg-[var(--color-accent-soft)] flex items-center justify-center text-[var(--color-accent)] font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-[var(--color-text)] truncate">{user.name}</span>
                            {/* Indicador admin solo en movil (la columna Admin se oculta) */}
                            {(user.is_admin || user.admin_role) && (
                              <Shield size={13} className="sm:hidden shrink-0 text-purple-500" />
                            )}
                          </div>
                          {/* Email plegado bajo el nombre solo en movil (la columna Email se oculta) */}
                          <span className="md:hidden block text-xs text-[var(--color-text-muted)] truncate">{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell p-4 text-[var(--color-text-muted)]">{user.email}</td>
                    <td className="hidden lg:table-cell p-4 text-[var(--color-text-muted)]">{formatDate(user.created_at)}</td>
                    <td className="p-3 sm:p-4">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        user.subscription_plan === 'premium'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                          : user.subscription_plan === 'trial'
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                      }`}>
                        {user.subscription_plan === 'premium' ? 'Premium' : user.subscription_plan === 'trial' ? 'Trial' : 'Free'}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell p-4">
                      {user.is_admin || user.admin_role ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                          <Shield size={12} />
                          Admin
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)] text-sm">-</span>
                      )}
                    </td>
                    <td className="p-3 sm:p-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {!user.is_admin && !user.admin_role && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImpersonate(user);
                            }}
                            disabled={impersonatingId === user.id}
                            title={`Ver la app como ${user.name}`}
                            className="flex items-center gap-1.5 text-sm text-[var(--color-text-muted)] hover:text-amber-600 transition-colors disabled:opacity-50"
                          >
                            {impersonatingId === user.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <UserCog size={15} />
                            )}
                            <span className="hidden lg:inline">Ver como</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewUser(user);
                          }}
                          className="text-[var(--color-accent)] hover:underline text-sm"
                        >
                          Ver detalles
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[var(--color-border)]">
            <p className="text-sm text-[var(--color-text-muted)]">
              Página {currentPage} de {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={currentPage === 1}
                className="btn btn-secondary py-2 px-3 disabled:opacity-50"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary py-2 px-3 disabled:opacity-50"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      <AnimatePresence>
        {showModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[var(--color-surface)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
                <h2 className="text-lg font-semibold text-[var(--color-text)]">
                  {isEditing ? 'Editar Usuario' : 'Detalles del Usuario'}
                </h2>
                <div className="flex items-center gap-2">
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-2 rounded-lg hover:bg-[var(--color-bg)] transition-colors text-[var(--color-accent)]"
                    >
                      <Edit3 size={20} />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setIsEditing(false);
                    }}
                    className="p-2 rounded-lg hover:bg-[var(--color-bg)] transition-colors"
                  >
                    <X size={20} className="text-[var(--color-text-muted)]" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-4 space-y-4">
                {/* User Avatar */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-[var(--color-accent-soft)] flex items-center justify-center text-[var(--color-accent)] text-2xl font-bold">
                    {(editForm.name || selectedUser.name).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    {!isEditing ? (
                      <>
                        <h3 className="text-xl font-semibold text-[var(--color-text)]">{selectedUser.name}</h3>
                        <p className="text-[var(--color-text-muted)]">{selectedUser.email}</p>
                      </>
                    ) : (
                      <p className="text-sm text-[var(--color-text-muted)]">ID: {selectedUser.id}</p>
                    )}
                  </div>
                </div>

                {/* Edit Form */}
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                        Nombre
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="input"
                        placeholder="Nombre completo"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="input"
                        placeholder="correo@ejemplo.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="input"
                        placeholder="+34 600 000 000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                        Plan de Suscripcion
                      </label>
                      <select
                        value={editForm.subscription_plan}
                        onChange={(e) => setEditForm({ ...editForm, subscription_plan: e.target.value as SubscriptionPlan })}
                        className="input"
                      >
                        <option value="free">Free</option>
                        <option value="trial">Trial (7 dias)</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>

                    {/* Success/Error Messages */}
                    {saveSuccess && (
                      <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm">
                        Cambios guardados correctamente
                      </div>
                    )}
                    {saveError && (
                      <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                        {saveError}
                      </div>
                    )}

                    {/* Save/Cancel Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveUser}
                        disabled={actionLoading === 'save'}
                        className="btn btn-primary flex-1"
                      >
                        {actionLoading === 'save' ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Save size={18} />
                            Guardar Cambios
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditForm({
                            name: selectedUser.name || '',
                            email: selectedUser.email || '',
                            phone: (selectedUser as any).phone || '',
                            subscription_plan: selectedUser.subscription_plan || 'free',
                          });
                        }}
                        className="btn btn-secondary"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Info Cards */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                        <div className="flex items-center gap-2 text-[var(--color-text-muted)] mb-1">
                          <Calendar size={14} />
                          <span className="text-xs">Registro</span>
                        </div>
                        <p className="text-sm font-medium text-[var(--color-text)]">
                          {formatDate(selectedUser.created_at)}
                        </p>
                      </div>
                      <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                        <div className="flex items-center gap-2 text-[var(--color-text-muted)] mb-1">
                          <Activity size={14} />
                          <span className="text-xs">Registros</span>
                        </div>
                        <p className="text-sm font-medium text-[var(--color-text)]">
                          {userStats?.total || 0} totales
                        </p>
                      </div>
                    </div>

                    {/* Phone if available */}
                    {(selectedUser as any).phone && (
                      <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Teléfono</p>
                        <p className="text-sm font-medium text-[var(--color-text)]">
                          {(selectedUser as any).phone}
                        </p>
                      </div>
                    )}

                    {/* Stats */}
                    {userStats && (
                      <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                        <h4 className="font-medium text-[var(--color-text)] mb-3">Estadísticas</h4>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-orange-500">{userStats.headaches || 0}</p>
                            <p className="text-xs text-[var(--color-text-muted)]">Dolores</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-red-500">{userStats.tensions || 0}</p>
                            <p className="text-xs text-[var(--color-text-muted)]">Tensiones</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-blue-500">{userStats.lloros || 0}</p>
                            <p className="text-xs text-[var(--color-text-muted)]">Lloros</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Temp Password */}
                    {tempPassword && (
                      <div className="p-4 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                          Nueva contraseña temporal:
                        </p>
                        <code className="block p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-yellow-800 dark:text-yellow-200 font-mono text-lg">
                          {tempPassword}
                        </code>
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                          El usuario deberá cambiarla en su próximo inicio de sesión.
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <button
                        onClick={() => handleToggleAdmin(selectedUser)}
                        disabled={actionLoading === `admin-${selectedUser.id}`}
                        className={`btn flex-1 ${
                          selectedUser.is_admin
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                            : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                        }`}
                      >
                        {actionLoading === `admin-${selectedUser.id}` ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : selectedUser.is_admin ? (
                          <>
                            <ShieldOff size={18} />
                            Quitar Admin
                          </>
                        ) : (
                          <>
                            <Shield size={18} />
                            Hacer Admin
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleResetPassword(selectedUser)}
                        disabled={actionLoading === `password-${selectedUser.id}`}
                        className="btn bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-1"
                      >
                        {actionLoading === `password-${selectedUser.id}` ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Key size={18} />
                            Reset Password
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleDeleteUser(selectedUser)}
                        disabled={actionLoading === `delete-${selectedUser.id}`}
                        className="btn bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                      >
                        {actionLoading === `delete-${selectedUser.id}` ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
